import { useCallback, useRef, useState } from "react";
import { queryClient } from "./query";
import { getQueryKey } from "@trpc/react-query";
import type {
    InferQueryLikeData,
    InferQueryLikeInput,
} from "@trpc/react-query/shared";

/**
 * Options for useAutosave hook.
 */
export interface UseAutosaveOptions<TQuery, TMutationInput> {
    /** tRPC mutation procedure (e.g., trpc.protocols.update) */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutation: { useMutation: (opts?: any) => { mutate: (input: TMutationInput) => void } };

    /** Query procedure from trpc (e.g., trpc.protocols.get) */
    query: TQuery;

    /** Input for the query (e.g., protocolId) */
    queryInput: InferQueryLikeInput<TQuery>;

    /** Transform mutation input to cache update */
    updateCache: (
        input: TMutationInput,
        old: InferQueryLikeData<TQuery> | undefined,
    ) => InferQueryLikeData<TQuery> | undefined;

    /** Called on mutation error */
    onError?: (error: { message: string }) => void;

    /** Called after successful mutation (before invalidation) */
    onSuccess?: () => void;

    /** Debounce delay in ms (default: 500) */
    delay?: number;
}

export interface UseAutosaveResult<TMutationInput> {
    /** Save with immediate optimistic update */
    save: (input: TMutationInput) => void;
    /** Whether a save is pending */
    isPending: boolean;
    /** Force pending save immediately */
    flush: () => void;
    /** Cancel pending save and rollback */
    cancel: () => void;
}

/**
 * Autosave hook with immediate optimistic updates and debounced mutations.
 *
 * Use for Interaction Mode #3 (Configuration Editors) - always-editable inputs
 * that autosave on change. Eliminates the need for local state mirroring server data.
 *
 * Features:
 * - Immediate optimistic update to query cache (instant UI feedback)
 * - Debounced server mutation (reduces API calls during rapid typing)
 * - Automatic rollback on error
 * - Merges rapid changes within debounce window
 *
 * @example
 * ```tsx
 * const { data: protocol } = trpc.fermentations.protocols.get.useQuery(protocolId);
 *
 * const { save } = useAutosave({
 *   mutation: trpc.fermentations.protocols.update,
 *   query: trpc.fermentations.protocols.get,
 *   queryInput: protocolId,
 *   updateCache: (input, old) => old ? { ...old, ...filterDefined(input) } : old,
 *   onError: (err) => toast.error(err.message),
 * });
 *
 * <Input
 *   value={protocol.name}
 *   onChange={(e) => save({ id: protocolId, name: e.target.value })}
 * />
 * ```
 */
export function useAutosave<TQuery, TMutationInput>(
    options: UseAutosaveOptions<TQuery, TMutationInput>,
): UseAutosaveResult<TMutationInput> {
    type TData = InferQueryLikeData<TQuery>;

    const {
        mutation: mutationProcedure,
        query,
        queryInput,
        updateCache,
        onError,
        onSuccess,
        delay = 500,
    } = options;

    // Get the query key using tRPC's getQueryKey helper
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryKey = getQueryKey(query as any, queryInput, "query");

    // Refs for rollback
    const snapshotRef = useRef<TData | undefined>(undefined);
    const hasSnapshotRef = useRef(false);

    const rollback = useCallback(() => {
        if (hasSnapshotRef.current) {
            queryClient.setQueryData(queryKey, snapshotRef.current);
            hasSnapshotRef.current = false;
            snapshotRef.current = undefined;
        }
    }, [queryKey]);

    // Keep rollback ref current for mutation callback
    const rollbackRef = useRef(rollback);
    rollbackRef.current = rollback;

    // Keep onSuccess ref current for mutation callback
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;

    // Create mutation with error handling and invalidation
    const mutation = mutationProcedure.useMutation({
        onSuccess: () => {
            onSuccessRef.current?.();
        },
        onError: (error: { message: string }) => {
            rollbackRef.current();
            onError?.(error);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    // Debounce state
    const [isPending, setIsPending] = useState(false);
    const pendingRef = useRef<TMutationInput | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimeout_ = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const flush = useCallback(() => {
        clearTimeout_();
        if (pendingRef.current !== null) {
            const input = pendingRef.current;
            pendingRef.current = null;
            setIsPending(false);
            mutation.mutate(input);
        }
    }, [clearTimeout_, mutation]);

    const cancel = useCallback(() => {
        clearTimeout_();
        pendingRef.current = null;
        setIsPending(false);
        rollback();
    }, [clearTimeout_, rollback]);

    const save = useCallback(
        (input: TMutationInput) => {
            clearTimeout_();

            // Snapshot before first optimistic update
            if (!hasSnapshotRef.current) {
                snapshotRef.current = queryClient.getQueryData<TData>(queryKey);
                hasSnapshotRef.current = true;
            }

            // Immediate optimistic update
            queryClient.setQueryData<TData>(queryKey, (old) => updateCache(input, old));

            // Merge with pending (object spread)
            pendingRef.current =
                pendingRef.current !== null
                    ? ({ ...pendingRef.current, ...input } as TMutationInput)
                    : input;

            setIsPending(true);

            // Schedule mutation
            timeoutRef.current = setTimeout(() => {
                if (pendingRef.current !== null) {
                    const final = pendingRef.current;
                    pendingRef.current = null;
                    setIsPending(false);
                    mutation.mutate(final);
                }
            }, delay);
        },
        [delay, queryKey, updateCache, clearTimeout_, mutation],
    );

    return { save, isPending, flush, cancel };
}

/** Filter undefined values from an object */
export function filterDefined<T extends Record<string, unknown>>(
    obj: T,
): Partial<T> {
    return Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== undefined),
    ) as Partial<T>;
}
